import { styled, keyframes } from '@mui/material/styles';
import { Colors } from 'utils';

export const PANEL_WIDTH = 440;

const Palette = {
    accent: Colors.ThemeBlue,
    accentSoft: Colors.BackgroundWater,
    accentGradient: `linear-gradient(135deg, ${Colors.ThemeBlue} 0%, ${Colors.ThemePurple} 100%)`,
    surface: '#FFFFFF',
    canvas: Colors.BackgroundLight,
    border: '#E4E9F0',
    text: '#1D2433',
    textMuted: Colors.TextPrimary,
    danger: '#D92D20',
    dangerSoft: '#FEF3F2',
};

const blink = keyframes`50% { opacity: 0; }`;
const bounce = keyframes`0%, 80%, 100% { transform: translateY(0); opacity: .4; } 40% { transform: translateY(-4px); opacity: 1; }`;

/* ── Shell ─────────────────────────────────────────────── */

/** Height of the app's top bar (`menuBar/core/topBar.js`). */
const APP_BAR_HEIGHT = 64;

export const Panel = styled('aside')(({ theme }) => ({
    position: 'fixed',
    // Below the top bar, so "Ask AI", notifications and the profile menu stay reachable while it is open.
    top: APP_BAR_HEIGHT,
    right: 0,
    bottom: 0,
    // Width comes from usePanelWidth via inline style; this is the pre-hydration default.
    width: PANEL_WIDTH,
    maxWidth: '100vw',
    display: 'flex',
    flexDirection: 'column',
    background: Palette.canvas,
    borderLeft: `1px solid ${Palette.border}`,
    boxShadow: '-12px 0 32px rgba(16, 24, 40, 0.10)',
    // Above the page and the side menu, below MUI modals — dialogs opened from the page still sit on top.
    zIndex: theme.zIndex.drawer + 50,
    // On a phone the panel is the whole screen; it has its own close button.
    [theme.breakpoints.down('sm')]: { top: 0 },
}));

/** Grab strip on the panel's left edge. Wider than it looks so it's easy to hit. */
export const ResizeHandle = styled('div', { shouldForwardProp: (prop) => prop !== 'active' })(({ active }) => ({
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: -5,
    width: 10,
    cursor: 'col-resize',
    zIndex: 1,
    touchAction: 'none',
    outline: 'none',
    '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 4,
        width: 2,
        background: active ? Palette.accent : 'transparent',
        transition: 'background .15s',
    },
    '&:hover::after, &:focus-visible::after': { background: Palette.accent },
}));

export const Header = styled('header')({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 12px 14px 20px',
    minHeight: 64,
    background: Palette.surface,
    borderBottom: `1px solid ${Palette.border}`,
});

export const BrandMark = styled('div', { shouldForwardProp: (prop) => prop !== 'size' })(({ size = 36 }) => ({
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: size / 3.2,
    display: 'grid',
    placeItems: 'center',
    color: '#FFFFFF',
    background: Palette.accentGradient,
    boxShadow: '0 4px 12px rgba(12, 102, 228, 0.28)',
    '& svg': { fontSize: size * 0.55 },
}));

export const HeaderText = styled('div')({
    flex: 1,
    minWidth: 0,
    '& h2': { margin: 0, fontSize: 15, fontWeight: 700, color: Palette.text, lineHeight: '20px' },
    '& p': { margin: 0, fontSize: 12, color: Palette.textMuted, lineHeight: '16px' },
});

export const Body = styled('div')({
    flex: 1,
    overflowY: 'auto',
    padding: '20px 20px 8px',
    scrollbarWidth: 'thin',
});

/* ── Empty state ───────────────────────────────────────── */

export const Welcome = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '40px 8px 16px',
    gap: 8,
    '& h3': { margin: '12px 0 0', fontSize: 20, fontWeight: 700, color: Palette.text },
    '& p': { margin: 0, fontSize: 14, color: Palette.textMuted, maxWidth: 320, lineHeight: '20px' },
    '& p.welcome-note': { marginTop: 8, fontSize: 12, lineHeight: '18px' },
});

export const WelcomeList = styled('ul')({
    margin: '8px 0 0',
    padding: '12px 16px 12px 32px',
    textAlign: 'left',
    borderRadius: 12,
    background: Palette.surface,
    border: `1px solid ${Palette.border}`,
    fontSize: 13,
    lineHeight: '20px',
    color: Palette.text,
    maxWidth: 320,
    boxSizing: 'border-box',
    width: '100%',
});

/* ── Messages ──────────────────────────────────────────── */

export const MessageList = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
});

export const UserBubble = styled('div')({
    alignSelf: 'flex-end',
    maxWidth: '85%',
    padding: '10px 14px',
    borderRadius: '16px 16px 4px 16px',
    background: Palette.accent,
    color: '#FFFFFF',
    fontSize: 14,
    lineHeight: '20px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
});

export const AssistantRow = styled('div')({
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    '&:hover [data-actions], & [data-actions]:focus-within': { opacity: 1 },
});

export const AssistantBody = styled('div')({
    flex: 1,
    minWidth: 0,
    paddingTop: 4,
});

export const Markdown = styled('div')({
    fontSize: 14,
    lineHeight: '22px',
    color: Palette.text,
    wordBreak: 'break-word',
    '& > :first-of-type': { marginTop: 0 },
    '& > :last-child': { marginBottom: 0 },
    '& p': { margin: '0 0 10px' },
    '& h3, & h4, & h5, & h6': { margin: '16px 0 6px', fontWeight: 700, lineHeight: 1.35 },
    '& h3': { fontSize: 17 },
    '& h4': { fontSize: 15 },
    '& h5, & h6': { fontSize: 14 },
    '& ul, & ol': { margin: '0 0 10px', paddingLeft: 22 },
    '& li': { margin: '3px 0' },
    '& li > p': { margin: 0 },
    '& li > ul, & li > ol': { margin: '4px 0 0' },
    '& a': { color: Palette.accent, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
    '& a.md-app-link': {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        padding: '0 8px',
        borderRadius: 999,
        background: Palette.accentSoft,
        lineHeight: '22px',
        whiteSpace: 'nowrap',
        transition: 'background .15s',
        '&::after': { content: '"→"', fontWeight: 700 },
        '&:hover': { textDecoration: 'none', background: '#D8E5FB' },
    },
    '& strong': { fontWeight: 700 },
    '& code': {
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 12.5,
        padding: '1px 5px',
        borderRadius: 5,
        background: '#EEF1F6',
    },
    '& pre': {
        margin: '0 0 10px',
        padding: '12px 14px',
        borderRadius: 10,
        background: '#1D2433',
        color: '#E6EAF2',
        overflowX: 'auto',
        '& code': { padding: 0, background: 'none', color: 'inherit' },
    },
    '& blockquote': {
        margin: '0 0 10px',
        padding: '2px 0 2px 12px',
        borderLeft: `3px solid ${Palette.border}`,
        color: Palette.textMuted,
    },
    '& hr': { border: 0, borderTop: `1px solid ${Palette.border}`, margin: '14px 0' },
    '& .md-table': { overflowX: 'auto', margin: '0 0 10px', borderRadius: 10, border: `1px solid ${Palette.border}` },
    '& table': { borderCollapse: 'collapse', width: '100%', fontSize: 13, background: Palette.surface },
    '& th, & td': { padding: '8px 10px', textAlign: 'left', borderBottom: `1px solid ${Palette.border}`, verticalAlign: 'top' },
    '& th': { fontWeight: 700, background: Palette.canvas },
    '& tr:last-child td': { borderBottom: 0 },
});

export const Caret = styled('span')({
    display: 'inline-block',
    width: 7,
    height: 15,
    marginLeft: 2,
    verticalAlign: 'text-bottom',
    borderRadius: 1,
    background: Palette.accent,
    animation: `${blink} 1s steps(1) infinite`,
});

export const TypingDots = styled('div')({
    display: 'inline-flex',
    gap: 4,
    padding: '8px 0',
    '& span': {
        width: 7,
        height: 7,
        borderRadius: '50%',
        background: Palette.accent,
        animation: `${bounce} 1.2s infinite ease-in-out`,
    },
    '& span:nth-of-type(2)': { animationDelay: '.15s' },
    '& span:nth-of-type(3)': { animationDelay: '.3s' },
});

/** A line from the app between messages, e.g. that the conversation above is no longer remembered. */
export const Notice = styled('div')({
    alignSelf: 'center',
    maxWidth: '90%',
    padding: '6px 12px',
    borderRadius: 8,
    background: Palette.accentSoft,
    color: Palette.accent,
    fontSize: 12,
    textAlign: 'center',
});

export const Activity = styled('div')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 6,
    padding: '4px 10px',
    borderRadius: 999,
    background: Palette.accentSoft,
    color: Palette.accent,
    fontSize: 12,
    fontWeight: 600,
});

export const MessageActions = styled('div')({
    display: 'flex',
    gap: 2,
    marginTop: 4,
    marginLeft: -6,
    opacity: 0,
    transition: 'opacity .15s',
});

export const ErrorBox = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginTop: 4,
    padding: '10px 12px',
    borderRadius: 10,
    background: Palette.dangerSoft,
    color: Palette.danger,
    fontSize: 13,
    lineHeight: '18px',
    '& > span': { flex: 1 },
});

/* ── Composer ──────────────────────────────────────────── */

export const ComposerArea = styled('div')({
    padding: '8px 16px 12px',
});

export const ComposerBox = styled('div')({
    display: 'flex',
    alignItems: 'flex-end',
    gap: 8,
    padding: '8px 8px 8px 14px',
    borderRadius: 16,
    background: Palette.surface,
    border: `1px solid ${Palette.border}`,
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
    transition: 'border-color .15s, box-shadow .15s',
    '&:focus-within': {
        borderColor: Palette.accent,
        boxShadow: `0 0 0 3px ${Palette.accentSoft}`,
    },
    '& .MuiInputBase-root': { flex: 1, fontSize: 14, lineHeight: '20px', color: Palette.text, padding: '6px 0' },
});

export const SendButton = styled('button')({
    all: 'unset',
    flexShrink: 0,
    width: 34,
    height: 34,
    borderRadius: 10,
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    color: '#FFFFFF',
    background: Palette.accent,
    transition: 'background .15s, transform .1s',
    '&:hover': { background: '#0A55BE' },
    '&:active': { transform: 'scale(0.95)' },
    '&:focus-visible': { outline: `2px solid ${Palette.accent}`, outlineOffset: 2 },
    '&:disabled': { cursor: 'default', background: '#C9D3E0' },
    '& svg': { fontSize: 20 },
});

export const ComposerFooter = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    marginTop: 6,
    padding: '0 4px',
    fontSize: 11,
    color: Palette.textMuted,
});

export const QuotaRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 6,
    padding: '0 4px',
    fontSize: 11,
    color: Palette.textMuted,
    '& > span:last-of-type': { flexShrink: 0, fontWeight: 600 },
});

export const QuotaTrack = styled('div')({
    flex: 1,
    height: 4,
    borderRadius: 2,
    background: Palette.border,
    overflow: 'hidden',
});

export const QuotaFill = styled('div')({
    height: '100%',
    borderRadius: 2,
    transition: 'width .3s ease',
});

/* ── Top bar trigger ───────────────────────────────────── */

export const TriggerButton = styled('button', { shouldForwardProp: (prop) => prop !== 'active' })(({ active }) => ({
    all: 'unset',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 36,
    padding: '0 14px 0 10px',
    marginRight: 16,
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 600,
    color: active ? '#FFFFFF' : Palette.accent,
    background: active ? Palette.accentGradient : Palette.accentSoft,
    transition: 'background .2s, color .2s, box-shadow .2s',
    '&:hover': { boxShadow: '0 2px 10px rgba(12, 102, 228, 0.25)' },
    '&:focus-visible': { outline: `2px solid ${Palette.accent}`, outlineOffset: 2 },
    '& svg': { fontSize: 18 },
}));
